// The site's own dictionary: words the commentary leans on, defined once and
// marked wherever they occur — in the commentary and in the ESV text beside it.
//
// One matcher, applied in two places. `markdown.ts` wraps a term as it renders
// the commentary; `client/src/glossary/markTerms.ts` wraps it in the ESV's
// HTML, which we never store and so can only decorate in the browser. Both ask
// `find`, so a word cannot be marked in one and missed in the other.
/**
 * How two forms are compared. Case, a curly apostrophe against a straight one
 * and a run of spaces do not count: the typographer curls the author's
 * apostrophes, the ESV sets its own, and neither should decide whether
 * "Nehemiah's" is found.
 */
export function glossaryKey(form) {
    return form.toLowerCase().replace(/’/g, "'").replace(/\s+/g, " ").trim();
}
/** "Steadfast love" → "steadfast-love": the entry's anchor on /glossary. */
export function glossaryId(term) {
    return term
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .replace(/['’]/g, "")
        .replace(/[^\p{L}\p{N}]+/gu, "-")
        .replace(/^-+|-+$/g, "");
}
/** A form as a pattern: either apostrophe, and any space between words. */
function pattern(key) {
    return key
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        .replace(/'/g, "['’]")
        .replace(/ /g, "\\s+");
}
/** The attributes of a marked term, the same from the renderer and the DOM walker. */
export function termAttrs(id) {
    return [
        ["class", "term"],
        ["data-term", id],
        ["tabindex", "0"],
        ["role", "button"],
    ];
}
/** Null when there is nothing to look for, so callers can skip the walk entirely. */
export function termMatcher(entries) {
    const owner = new Map();
    for (const e of entries) {
        for (const form of [e.term, ...e.forms]) {
            const key = glossaryKey(form);
            if (key && !owner.has(key))
                owner.set(key, e.id);
        }
    }
    if (!owner.size)
        return null;
    // Longest first: the regex takes the first alternative that fits, so with
    // "love" ahead of "love of God" the phrase would only ever be found as its
    // first word. The lookarounds are whole-word edges that understand more than
    // ASCII: "Nehemiah" is found in "Nehemiah’s" but "love" is not in "beloved".
    const alternatives = [...owner.keys()].sort((a, b) => b.length - a.length).map(pattern);
    const re = new RegExp(`(?<![\\p{L}\\p{N}])(?:${alternatives.join("|")})(?![\\p{L}\\p{N}])`, "giu");
    return {
        find(text) {
            const out = [];
            for (const m of text.matchAll(re)) {
                const id = owner.get(glossaryKey(m[0]));
                if (id)
                    out.push({ start: m.index, end: m.index + m[0].length, id });
            }
            return out;
        },
    };
}
//# sourceMappingURL=glossary.js.map